import { 
    Card, Box, Typography, AppBar, Toolbar, Button, IconButton, 
    Dialog, DialogTitle, FormControl, InputLabel, Select, MenuItem, 
    Tooltip, Chip, Stack 
} from "@mui/material";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";

// Icons
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Components & Logic
import { useEffect, useState } from "react";
import { IForecast } from "../../interface/forecast";
import { RootState } from "../../redux/reducers";
import { deleteOne } from "../../fetchs/delete";
import { sessionEnd, showSnackbar } from "../../redux/actions";
import { addForecast, duplicateForecast, updateForecast } from "../../tools/forecast/addUpdateForecast";
import getForecastsData from "../../tools/forecast/getForecastsData";
import getForecastDates from "../../tools/forecast/getForecastDates";
import AddUpdateForecast from "../../components/Forecast/AddUpdate/AddUpdateForecast";
import DuplicateForecast from "../../components/Forecast/AddUpdate/DuplicateForecast";
import AlerteDialog from "../../components/AlerteDialog/AlerteDialog";

const HomeForecast = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = useSelector((state: RootState) => state.user.token);

    // States
    const [forecastData, setForecastData] = useState<IForecast[]>([]);
    const [error, setError] = useState('');
    const [openCopyModal, setOpenCopyModal] = useState(false);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [forecastId, setForecastId] = useState("");
    const [forecastInfo, setForecastInfo] = useState<IForecast | undefined>();
    const [selectedSociety, setSelectedSociety] = useState("all");
    const [actionType, setActionType] = useState<"delete" | "close" | null>(null);

    // --- COLUMNS DEFINITION ---
    const columns: GridColDef[] = [
        { 
            field: "name", 
            headerName: "Nom du Prévisionnel", 
            flex: 2,
            renderCell: (params) => (
                <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>{params.value}</Typography>
            )
        },
        { field: "period", headerName: "Période", flex: 1.5 },
        { 
            field: "society", 
            headerName: "Société", 
            flex: 1.5,
            renderCell: (params) => (
                <Chip label={params.value} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
            )
        },
        { field: "updatedAt", headerName: "Dernière modif.", flex: 1, type: "date", 
          valueGetter: (params) => params.value ? new Date(params.value.split("/").reverse().join("-")) : null 
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Modifier les infos">
                        <IconButton onClick={(e) => { e.stopPropagation(); setForecastInfo(params.row); setOpenCreateModal(true); }}>
                            <EditTwoToneIcon sx={{ color: "#f59e0b" }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                        <IconButton onClick={(e) => { e.stopPropagation(); setForecastId(params.row.id); setActionType("delete"); setOpenDeleteModal(true); }}>
                            <DeleteTwoToneIcon sx={{ color: "#ef4444" }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        }
    ];

    // --- ROWS PREPARATION ---
    const rows: GridRowsProp = forecastData
        .filter(f => selectedSociety === "all" || f.society === selectedSociety)
        .map((f) => {
            const dates = getForecastDates(f.startDate, f.lenght);
            return {
                id: f._id,
                name: f.name || dates,
                period: dates,
                society: f.society,
                updatedAt: f.updatedAt,
                startDate: f.startDate,
                lenght: f.lenght,
                versions: f.versions
            }
        });

    // --- LOGIC ---
    const getInitialData = () => getForecastsData(setForecastData, token, dispatch, setError);

    useEffect(() => { getInitialData(); }, []);

    const deleteForecast = async () => {
        if (!token) return;
        const res = await deleteOne(token, "/forecast/", forecastId);
        if (res.status === 200) {
            dispatch(showSnackbar("Prévisionnel supprimé !", "success"));
            setOpenDeleteModal(false);
            getInitialData();
        } else if (res.status === 401) {
            dispatch(sessionEnd());
        }
    };

    const confirmClose = () => {
        setForecastInfo(undefined);
        setOpenCreateModal(false);
        setOpenCopyModal(false);
        setOpenDeleteModal(false);
    };

    return (
        <Box sx={{ flexGrow: 1, bgcolor: "#f1f5f9", minHeight: "100vh" }}>
            <AppBar position="static" sx={{ bgcolor: "#0f172a", elevation: 0 }}>
                <Toolbar>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "rgba(255,255,255,0.9)" }}>Gestion des Prévisionnels</Typography>
                </Toolbar>
            </AppBar>

            <Box sx={{ p: 3 }}>
                {/* --- BARRE D'ACTIONS --- */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center" }}>
                    <FormControl size="small" sx={{ minWidth: 250, bgcolor: "white", borderRadius: 1 }}>
                        <InputLabel>Filtrer par société</InputLabel>
                        <Select
                            value={selectedSociety}
                            label="Filtrer par société"
                            onChange={(e) => setSelectedSociety(e.target.value)}
                        >
                            <MenuItem value="all">Toutes les sociétés</MenuItem>
                            {["ACTEAM-IT", "1, 2, 3 PORTAGE", "ACTIMM-RE", "ACTIMM-INV", "MACELIA - INV"].map(s => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Stack direction="row" spacing={2}>
                        <Button 
                            variant="outlined" 
                            startIcon={<ContentCopyIcon />}
                            onClick={() => rows.length > 0 ? setOpenCopyModal(true) : dispatch(showSnackbar("Aucun modèle disponible", "error"))}
                            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
                        >
                            Dupliquer existant
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<AddCircleIcon />}
                            onClick={() => { setForecastInfo(undefined); setOpenCreateModal(true); }}
                            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700,  }}
                        >
                            Nouveau Prévisionnel
                        </Button>
                    </Stack>
                </Box>

                {/* --- TABLEAU PRINCIPAL --- */}
                <Card sx={{ borderRadius: 4, height: "calc(100vh - 250px)", p: 1, boxShadow: "0px 4px 20px rgba(0,0,0,0.05)" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        onRowClick={(params) => navigate(`/prévisionnel/${params.id}`)}
                        sx={{
                            border: "none",
                            '& .MuiDataGrid-row:hover': { cursor: 'pointer', bgcolor: "#f8fafc" },
                            '& .MuiDataGrid-columnHeaders': { bgcolor: "#f1f5f9", borderRadius: 2 },
                        }}
                    />
                </Card>
            </Box>

            {/* --- MODALES --- */}
            <Dialog open={openCreateModal} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <DialogTitle sx={{ p: 0, fontWeight: 800 }}>
                        {forecastInfo ? "Modifier" : "Créer"} le prévisionnel
                    </DialogTitle>
                    <IconButton onClick={() => { setActionType("close"); setOpenDeleteModal(true); }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <AddUpdateForecast 
                    setOpenModal={setOpenCreateModal} 
                    setForecastData={setForecastData} 
                    forecastInfo={forecastInfo} 
                    handleSubmit={forecastInfo ? updateForecast : addForecast} 
                />
            </Dialog>

            <Dialog open={openCopyModal} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <DialogTitle sx={{ p: 0, fontWeight: 800 }}>Dupliquer un modèle</DialogTitle>
                    <IconButton onClick={() => setOpenCopyModal(false)}><CloseIcon /></IconButton>
                </Box>
                <DuplicateForecast 
                    setOpenModal={setOpenCopyModal} 
                    forecastData={rows} 
                    handleSubmit={duplicateForecast} 
                    setForecastData={setForecastData} 
                />
            </Dialog>

            <AlerteDialog
                open={openDeleteModal}
                handleClose={() => setOpenDeleteModal(false)}
                deleteProject={actionType === "delete" ? deleteForecast : confirmClose}
                title={actionType === "delete" ? "Supprimer définitivement ?" : "Abandonner ?"}
                contentText={actionType === "delete" 
                    ? "Toutes les versions et données liées à ce prévisionnel seront supprimées." 
                    : "Les modifications non enregistrées seront perdues."}
            />
        </Box>
    );
};

export default HomeForecast;